import { Shield, Building2, Users, MessageSquare, Target, Send, PhoneCall, ShieldCheck, BadgeCheck, FlaskConical, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const COLORS = {
  emerald: "bg-emerald-50 text-emerald-700", rose: "bg-rose-50 text-rose-700", amber: "bg-amber-50 text-amber-700",
  indigo: "bg-indigo-50 text-indigo-700", slate: "bg-slate-100 text-slate-700",
};

// `to` = company-table params applied on click; `info` = informational only
const CARDS = [
  { key: "companies", label: "Total Companies", icon: Building2, color: "emerald", to: {} },
  { key: "active_companies", label: "Active Companies", icon: BadgeCheck, color: "emerald", to: { status: "active" } },
  { key: "suspended", label: "Suspended Companies", icon: Shield, color: "rose", to: { status: "suspended" } },
  { key: "test_companies", label: "Test / Demo Companies", icon: FlaskConical, color: "amber", to: { kind: "test" } },
  { key: "users", label: "Platform Users", icon: Users, color: "indigo", to: { sort: "user_count" }, hint: "Company Admins + Sales Associates. Opens companies sorted by users." },
  { key: "admins", label: "Company Admins", icon: ShieldCheck, color: "indigo", to: { sort: "user_count" }, hint: "Opens companies sorted by users" },
  { key: "associates", label: "Sales Associates", icon: Users, color: "emerald", to: { sort: "user_count" }, hint: "Opens companies sorted by users" },
  { key: "wa_connected", label: "WhatsApp Live · Verified", icon: PhoneCall, color: "emerald", to: { wa: "live_verified" }, hint: "Live Meta Cloud API connections whose last test succeeded" },
  { key: "wa_live_other", label: "WhatsApp Live · Error / Untested", icon: PhoneCall, color: "rose", to: { wa: "live_issue" }, hint: "Live connections whose last test failed or that were never tested" },
  { key: "wa_mock", label: "WhatsApp Mock (demo)", icon: PhoneCall, color: "amber", to: { wa: "mock" }, hint: "Simulated connections — no real messages sent" },
  { key: "contacts", label: "Total Contacts", icon: Users, color: "slate", to: { sort: "contact_count" }, hint: "Opens companies sorted by contacts" },
  { key: "leads", label: "Leads", icon: Target, color: "amber", to: { sort: "lead_count" }, hint: "Opens companies sorted by leads" },
  { key: "qualified", label: "Qualified Leads", icon: Target, color: "emerald", to: { sort: "lead_count" }, hint: "Status qualified, site visit, negotiation or won. Opens companies sorted by leads." },
  { key: "conversations", label: "Conversations", icon: MessageSquare, color: "indigo", info: "Platform-wide total. Open a company to see its conversations." },
  { key: "msgs_sent", label: "Messages Sent", icon: Send, color: "indigo", info: "All outbound messages (AI, human, campaign). See a company's Usage tab for date ranges." },
  { key: "msgs_received", label: "Messages Received", icon: MessageSquare, color: "slate", info: "All inbound customer messages. See a company's Usage tab for date ranges." },
  { key: "ai_convos", label: "AI-active Chats", icon: MessageSquare, color: "amber", info: "Conversations currently handled by the AI agent." },
  { key: "campaigns_active", label: "Active Campaigns", icon: Send, color: "emerald", info: "Campaigns with status running or scheduled." },
];

function CardBody({ s, value }) {
  return (
    <>
      <div className="flex items-start justify-between">
        <div className={`w-8 h-8 rounded-lg ${COLORS[s.color]} flex items-center justify-center mb-2`}><s.icon className="w-4 h-4" /></div>
        {s.info && <Info className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-2xl font-bold text-slate-900" data-testid={`stat-value-${s.key}`}>{value ?? 0}</div>
      <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
    </>
  );
}

export default function StatCards({ stats, onSelect }) {
  return (
    <TooltipProvider delayDuration={150}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="super-stats">
        {CARDS.map((s) => (
          <Tooltip key={s.key}>
            <TooltipTrigger asChild>
              {s.to ? (
                <Card role="button" tabIndex={0} data-testid={`stat-card-${s.key}`}
                  onClick={() => onSelect(s.to)} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect(s.to))}
                  className="p-4 border-slate-200 cursor-pointer transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:shadow-md hover:border-indigo-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                  <CardBody s={s} value={stats[s.key]} />
                </Card>
              ) : (
                <Card tabIndex={0} className="p-4 border-slate-200 bg-slate-50/60 cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400" data-testid={`stat-card-${s.key}`}>
                  <CardBody s={s} value={stats[s.key]} />
                </Card>
              )}
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">{s.info || s.hint || `Show ${s.label.toLowerCase()}`}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
